import { ColumnFilter, SortingState, Updater } from '@tanstack/react-table';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FilterData, FilterExtraInfo, findFilterById } from '@/ui/TanstackTable/Filter/Filters';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import {
  QUERY_PARAM_ORDER_DIR_KEY,
  QUERY_PARAM_ORDER_KEY,
} from '@/utils/query-params/useOrderByQueryParam';

import {
  QUERY_PARAM_PAGE_INDEX_KEY,
  QUERY_PARAM_PAGINATION_AFTER_KEY,
  QUERY_PARAM_PAGINATION_BEFORE_KEY,
} from './usePaginationState';
import { getPrefixedQueryParamKey } from './useQueryParams';

export const QUERY_PARAM_GLOBAL_FILTER_KEY = 'q';

const filterToString = <TData>(filterInfo: FilterData<TData>, filterValue: unknown) => {
  const { filterType } = filterInfo;
  switch (filterType) {
    case FilterType.STRING:
      return filterValue as string;
    case FilterType.BOOLEAN:
      return (filterValue as boolean) ? 'true' : 'false';
    case FilterType.DATE: {
      const dateValues = filterValue as Date[];
      if (!dateValues || dateValues.length === 0 || dateValues.length > 2) {
        return null;
      }
      if (dateValues.length === 2) {
        const firstDate = dateValues[0];
        const secondDate = dateValues[1];

        if (!firstDate && secondDate) {
          return `-${secondDate.getTime()}`;
        } else if (firstDate && secondDate) {
          return `${dateValues[0].getTime()}-${dateValues[1].getTime()}`;
        }
      }
      return `+${dateValues[0].getTime()}`;
    }
    case FilterType.UNSUPPORTED:
      console.warn(`Unsupported filter type: ${filterType}`);
      return filterValue as string;
    default: {
      const _exhaustiveCheck: never = filterType;
      console.error(`Unsupported filter type`, filterType);
      throw new Error(`Unsupported filter type`);
    }
  }
};

const stringToFilter = <TData>(filterInfo: FilterData<TData>, filterValue: string) => {
  const { filterType } = filterInfo;
  switch (filterType) {
    case FilterType.STRING:
      return filterValue;
    case FilterType.BOOLEAN:
      return filterValue === 'true';
    case FilterType.DATE: {
      const greaterThanMatch = filterValue.match(/^\+(\d{13})$/);
      const lessThanMatch = filterValue.match(/^-(\d{13})$/);
      const rangeMatch = filterValue.match(/^(\d{13})-(\d{13})$/);

      if (greaterThanMatch) {
        return [new Date(parseInt(greaterThanMatch[1], 10))];
      } else if (lessThanMatch) {
        return [new Date(parseInt(lessThanMatch[1], 10))];
      } else if (rangeMatch) {
        return [new Date(parseInt(rangeMatch[1], 10)), new Date(parseInt(rangeMatch[2], 10))];
      }
      return [new Date(parseInt(filterValue, 10))];
    }
    case FilterType.UNSUPPORTED:
      console.warn(`Unsupported filter type: ${filterType}`);
      return filterValue;
    default: {
      const _exhaustiveCheck: never = filterType;
      console.error(`Unsupported filter type`, filterType);
      throw new Error(`Unsupported filter type`);
    }
  }
};

export const useTableQueryParamsUtils = <TData>(
  filterExtraInfos: FilterExtraInfo<TData>,
  prefix: string | null
) => {
  const [_, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const writeColumnFiltersToQueryParams = useCallback(
    (newColumnFilters: ColumnFilter[], filterExtraInfos: FilterExtraInfo<TData>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        // NOTE: reset pagination when sorting changes
        newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_AFTER_KEY, prefix));
        newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_BEFORE_KEY, prefix));
        //NOTE: remove old filters
        columnFilters.forEach((filter) => {
          const filterInfo = findFilterById(filterExtraInfos, filter.id);
          const key = getPrefixedQueryParamKey(filterInfo?.queryParamKey ?? filter.id, prefix);
          newParams.delete(key);
        });
        newColumnFilters.forEach((filter) => {
          if (filter.value === null || filter.value === undefined) return;
          const filterInfo = findFilterById(filterExtraInfos, filter.id);
          const key = getPrefixedQueryParamKey(filterInfo?.queryParamKey ?? filter.id, prefix);

          if (!filterInfo) {
            newParams.set(key, filter.value as string);
            return;
          }
          const value = filterToString(filterInfo, filter.value);
          if (value !== null) newParams.set(key, value);
        });
        return newParams;
      });
    },
    [setSearchParams, filterExtraInfos, columnFilters, prefix]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        // NOTE: reset pagination when sorting changes
        newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_AFTER_KEY, prefix));
        newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_BEFORE_KEY, prefix));
        if (newSorting.length === 0) {
          newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_ORDER_KEY, prefix));
          newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_ORDER_DIR_KEY, prefix));
          return newParams;
        }

        const primarySort = newSorting[0];
        newParams.set(getPrefixedQueryParamKey(QUERY_PARAM_ORDER_KEY, prefix), primarySort.id);
        newParams.set(
          getPrefixedQueryParamKey(QUERY_PARAM_ORDER_DIR_KEY, prefix),
          primarySort.desc ? 'desc' : 'asc'
        );
        return newParams;
      });
      setSorting(newSorting);
    },
    [sorting, setSearchParams, prefix]
  );

  const onColumnFiltersChange = useCallback(
    (newColumnFilters: ColumnFilter[]) => {
      console.log('DEBUG >> ', newColumnFilters);
      writeColumnFiltersToQueryParams(newColumnFilters, filterExtraInfos);
      setColumnFilters(newColumnFilters);
    },
    [writeColumnFiltersToQueryParams, filterExtraInfos]
  );

  const onGlobalFilterChange = useCallback(
    (newGlobalFilter: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        // NOTE: reset pagination when sorting changes
        newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_AFTER_KEY, prefix));
        newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_PAGINATION_BEFORE_KEY, prefix));
        if (newGlobalFilter) {
          newParams.set(
            getPrefixedQueryParamKey(QUERY_PARAM_GLOBAL_FILTER_KEY, prefix),
            newGlobalFilter
          );
        } else {
          newParams.delete(getPrefixedQueryParamKey(QUERY_PARAM_GLOBAL_FILTER_KEY, prefix));
        }
        return newParams;
      });
      setGlobalFilter(newGlobalFilter);
    },
    [setSearchParams, prefix]
  );

  // NOTE: Load currentQuery params into state on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get(getPrefixedQueryParamKey(QUERY_PARAM_GLOBAL_FILTER_KEY, prefix)) ?? '';
    const orderBy = params.get(getPrefixedQueryParamKey(QUERY_PARAM_ORDER_KEY, prefix));
    const orderDir = params.get(getPrefixedQueryParamKey(QUERY_PARAM_ORDER_DIR_KEY, prefix));

    setGlobalFilter(q);
    if (orderBy && orderDir) {
      setSorting([
        {
          id: orderBy,
          desc: orderDir === 'desc',
        },
      ]);
    }

    const newColumnFilters: ColumnFilter[] = [];
    // NOTE: we don't want to use the prefix here because we want to ignore
    // these keys even for subtables
    const nonFilterKeys = [
      QUERY_PARAM_GLOBAL_FILTER_KEY,
      QUERY_PARAM_ORDER_KEY,
      QUERY_PARAM_ORDER_DIR_KEY,
      QUERY_PARAM_PAGINATION_AFTER_KEY,
      QUERY_PARAM_PAGINATION_BEFORE_KEY,
      QUERY_PARAM_PAGE_INDEX_KEY,
    ];
    for (const [key, value] of params.entries()) {
      const filterInfo = filterExtraInfos.find((info) => {
        return getPrefixedQueryParamKey(info.queryParamKey || info.filterId, prefix) === key;
      });
      const ignoreKey = nonFilterKeys.some((el) => key.includes(el));
      if (!filterInfo && ignoreKey) {
        continue; // Skip non-filter keys
      }

      if (!filterInfo) {
        newColumnFilters.push({
          id: key.replace(`${prefix}_`, ''),
          value: value,
        });
        continue;
      }
      newColumnFilters.push({
        id: filterInfo.filterId,
        value: stringToFilter(filterInfo, value),
      });
    }
    setColumnFilters(newColumnFilters);
  }, [filterExtraInfos, prefix]);

  return {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  };
};
